import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import {
  buildMappedInvoiceValidationCsv,
  ColumnMappingState,
  guessColumnMapping,
  INVOICE_VALIDATION_TEMPLATE_COLUMNS,
  InvoiceValidationTargetKey,
  isInvoiceValidationMappingComplete,
  isMappedColumnIndex,
  parseCsv,
  readFileAsText,
  stripBom,
} from 'src/pages/billing/utils/invoice-validation-csv';
import { AlertCircle } from 'lucide-react';

const UNMAPPED = -1;

export interface InvoiceValidationColumnMapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: File | null;
  /** Platform for this upload (required before mapping can be confirmed); sent as `source` on upload, not in the CSV. */
  defaultSource: string;
  onConfirm: (mappedFile: File) => void;
}

function initMappingFromGuess(headers: string[]): ColumnMappingState {
  const guessed = guessColumnMapping(headers);
  const state: ColumnMappingState = {};
  for (const col of INVOICE_VALIDATION_TEMPLATE_COLUMNS) {
    const g = guessed[col.key];
    state[col.key] = g !== undefined ? g : null;
  }
  return state;
}

const InvoiceValidationColumnMapModal: React.FC<InvoiceValidationColumnMapModalProps> = ({
  open,
  onOpenChange,
  file,
  defaultSource,
  onConfirm,
}) => {
  const [parseError, setParseError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [matrix, setMatrix] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColumnMappingState>({});

  const loadFile = useCallback(async (f: File | null) => {
    setParseError(null);
    setHeaders([]);
    setMatrix([]);
    setMapping({});
    if (!f) {
      return;
    }
    try {
      const text = stripBom(await readFileAsText(f));
      const rows = parseCsv(text);
      if (rows.length === 0) {
        setParseError('The file is empty.');
        return;
      }
      const hdrs = rows[0].map((h) => h.trim());
      if (hdrs.every((h) => !h)) {
        setParseError('No header row found.');
        return;
      }
      setHeaders(hdrs);
      setMatrix(rows);
      setMapping(initMappingFromGuess(hdrs));
    } catch {
      setParseError('Could not read this file. Try saving as UTF-8 CSV.');
    }
  }, []);

  useEffect(() => {
    if (open && file) {
      void loadFile(file);
    }
  }, [open, file, loadFile]);

  const previewRows = useMemo(() => matrix.slice(1, 4), [matrix]);

  const setTargetMapping = (key: InvoiceValidationTargetKey, columnIndex: number) => {
    setMapping((prev) => ({
      ...prev,
      [key]: columnIndex === UNMAPPED ? null : columnIndex,
    }));
  };

  const canSubmit = useMemo(
    () => isInvoiceValidationMappingComplete(mapping, defaultSource) && matrix.length > 1,
    [mapping, defaultSource, matrix.length],
  );

  const handleConfirm = () => {
    if (!file || !canSubmit) {
      return;
    }
    const csv = buildMappedInvoiceValidationCsv(matrix, mapping);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const mappedFile = new File([blob], file.name, { type: 'text/csv' });
    onConfirm(mappedFile);
    onOpenChange(false);
  };

  const duplicateIndices = useMemo(() => {
    const used = new Map<number, InvoiceValidationTargetKey[]>();
    for (const col of INVOICE_VALIDATION_TEMPLATE_COLUMNS) {
      const idx = mapping[col.key];
      if (!isMappedColumnIndex(idx)) {
        continue;
      }
      const list = used.get(idx) ?? [];
      list.push(col.key);
      used.set(idx, list);
    }
    return [...used.entries()].filter(([, keys]) => keys.length > 1);
  }, [mapping]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white w-[calc(100vw-1.5rem)] max-w-[min(1200px,calc(100vw-1.5rem))] max-h-[90vh] overflow-y-auto overflow-x-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            Map columns
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-left break-words">
            Match each expected field to a column from
            {' '}
            <span className="font-medium text-gray-800 break-all">
              {file?.name ?? 'your file'}
            </span>
            . The file sent to the server will use headers
            {' '}
            <code className="text-xs bg-gray-100 px-1 rounded">account_id</code>
            ,
            {' '}
            <code className="text-xs bg-gray-100 px-1 rounded">campaign_id</code>
            ,
            {' '}
            <code className="text-xs bg-gray-100 px-1 rounded">spend</code>
            ,
            {' '}
            <code className="text-xs bg-gray-100 px-1 rounded">impressions</code>
            . Platform is set next to Upload, not in the CSV.
          </DialogDescription>
        </DialogHeader>

        {parseError && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            {parseError}
          </div>
        )}

        {!parseError && headers.length > 0 && (
          <>
            <div className="rounded-md border border-gray-200 overflow-hidden min-w-0">
              <table className="w-full text-xs table-fixed">
                <colgroup>
                  <col className="w-[38%] max-w-[min(280px,40vw)]" />
                  <col className="w-[62%]" />
                </colgroup>
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-700">
                    <th className="px-3 py-2 font-medium align-top">
                      Expected field
                    </th>
                    <th className="px-3 py-2 font-medium align-top">
                      Your column
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {INVOICE_VALIDATION_TEMPLATE_COLUMNS.map((col) => {
                    const idx = mapping[col.key];
                    const selectValue = isMappedColumnIndex(idx) ? String(idx) : String(UNMAPPED);
                    return (
                      <tr key={col.key} className="border-t border-gray-200">
                        <td className="px-3 py-2 align-top min-w-0">
                          <span className="text-gray-900 font-medium break-words">
                            {col.label}
                          </span>
                          {col.required ? (
                            <span className="ml-1 text-red-600">*</span>
                          ) : null}
                          {col.key === 'impressions' && (
                            <p className="text-gray-500 font-normal mt-0.5 break-words">
                              Optional. Maps to impressions or quantity when present.
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top min-w-0">
                          <select
                            value={selectValue}
                            onChange={(e) =>
                              setTargetMapping(col.key, Number(e.target.value))}
                            className="w-full max-w-full min-w-0 h-9 rounded-md border border-gray-300 bg-white px-2 text-gray-800 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            aria-label={`Map column for ${col.label}`}
                            title={
                              isMappedColumnIndex(idx) && headers[idx] !== undefined
                                ? headers[idx]
                                : undefined
                            }
                          >
                            <option value={String(UNMAPPED)}>— Not mapped —</option>
                            {headers.map((h, i) => (
                              <option
                                key={`${i}-${h}`}
                                value={String(i)}
                                title={h || `Column ${i + 1}`}
                              >
                                {h || `(Column ${i + 1})`}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {duplicateIndices.length > 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 break-words">
                The same file column is mapped more than once:
                {' '}
                {duplicateIndices.map(([idx, keys]) => (
                  <span key={idx} className="inline-block mr-2 max-w-full">
                    {headers[idx] || `Column ${idx + 1}`}
                    {' '}
                    (
                    {keys.join(', ')}
                    )
                  </span>
                ))}
              </p>
            )}

            {previewRows.length > 0 && (
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Preview (first data rows)
                </p>
                <div className="overflow-x-auto rounded border border-gray-200 text-xs max-w-full">
                  <table className="border-collapse border-spacing-0">
                    <thead>
                      <tr className="bg-gray-50">
                        {headers.map((h, i) => (
                          <th
                            key={i}
                            className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-700 align-top whitespace-normal break-words min-w-[96px] max-w-[min(280px,26vw)]"
                          >
                            {h || `Col ${i + 1}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, ri) => (
                        <tr key={ri}>
                          {headers.map((_, ci) => (
                            <td
                              key={ci}
                              className="border-b border-gray-100 px-2 py-1.5 text-gray-600 align-top whitespace-normal break-words min-w-[96px] max-w-[min(280px,26vw)]"
                            >
                              {r[ci] ?? ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isInvoiceValidationMappingComplete(mapping, defaultSource) && (
              <p className="text-xs text-gray-600">
                Map Account id, Campaign id, and Spend. Select a platform before choosing a file. Impressions / qty is optional.
              </p>
            )}
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={handleConfirm}
            disabled={!canSubmit || Boolean(parseError)}
          >
            Use mapped file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceValidationColumnMapModal;
